// Reconstruct tables from positional text.
//
// Find a "Table N-N" caption, take the block of lines beneath it, cluster the
// text cells into columns by x-position, and parse the numeric cells.
//
// PDF table extraction is imperfect and this makes no pretence otherwise: a
// table the parser is unsure of is still emitted, marked low confidence, with
// the reasons recorded. Nothing is silently dropped.

import type { ExtractedTable } from '../../types';
import type { PageLayout, TextLine } from './pdfExtract';
import { isRunningLine } from './pdfExtract';

/**
 * A table number in any of the styles federal documents use: "Table 1" in an
 * audit report, "Table 3-2" in an M&V plan, "Table 6-1.1" in the FEMP
 * guidelines.
 */
const LABEL = String.raw`Table\s+\d+(?:[-–.]\d+)*[A-Za-z]?`;

/**
 * A caption line: the label at the start, then a separator. A bare space is not
 * enough — "Table 4-1 Cooling equipment ... are enabled continuously" and
 * "Table 1 lists the energy conservation measures" are sentences that happen to
 * begin with a cross-reference, not captions.
 */
const CAPTION = new RegExp(
  String.raw`^\s*(${LABEL})\s*(?::|\.\s|\t|—|–|\s{2,})\s*(.*)$`,
  'i',
);
/** The label alone on its line; the caption then follows on the next line. */
const BARE_LABEL = new RegExp(String.raw`^\s*(${LABEL})\s*[:.]?\s*$`, 'i');

/** Columns whose left edges are within this many points are the same column. */
const COLUMN_TOLERANCE = 14;
/** A single cell longer than this is prose, not a table row. */
const PROSE_CELL = 88;
/** Stop after this many lines; a table that long is almost certainly runaway. */
const MAX_TABLE_LINES = 40;

/** The label as printed, with whitespace tidied. */
function normaliseLabel(label: string): string {
  return label.replace(/\s+/g, ' ').trim();
}

/**
 * Identity for deduplication. "Table 3.2" and "Table 3-2" are the same table
 * numbered in two styles, while "Table 6-1" and "Table 6-1.1" are not — so
 * every separator is normalised, and none is dropped.
 */
function labelKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[–—]/g, '-')
    .replace(/\./g, '-')
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/** "$1,234" -> 1234 · "(500)" -> -500 · "12.5%" -> 12.5 · "1,266,002 kWh" -> 1266002 */
export function parseNumericCell(raw: string): number | null {
  const text = raw.trim();
  if (!text) return null;

  const negated = /^\(.*\)$/.test(text);
  const inner = negated ? text.slice(1, -1) : text;

  // Take the leading numeric token so units after a figure do not defeat it.
  const match = /^[$€£]?\s*(-?\d[\d,]*(?:\.\d+)?)\s*%?/.exec(inner.trim());
  if (!match) return null;

  const value = Number(match[1].replace(/,/g, ''));
  if (!Number.isFinite(value)) return null;
  return negated ? -Math.abs(value) : value;
}

function isBlank(line: TextLine): boolean {
  return line.text.trim().length === 0;
}

function looksLikeProse(line: TextLine): boolean {
  if (line.cells.length > 2) return false;
  return line.cells.some((c) => c.text.length > PROSE_CELL);
}

/** Assign every cell to a column by clustering left edges across all rows. */
function buildColumns(lines: TextLine[]): number[] {
  const edges = lines.flatMap((l) => l.cells.map((c) => c.x)).sort((a, b) => a - b);
  const columns: number[] = [];
  for (const x of edges) {
    const last = columns[columns.length - 1];
    if (last === undefined || x - last > COLUMN_TOLERANCE) columns.push(x);
  }
  return columns;
}

function toRow(line: TextLine, columns: number[]): string[] {
  const row = new Array<string>(columns.length).fill('');
  for (const cell of line.cells) {
    let best = 0;
    let bestDistance = Infinity;
    for (let i = 0; i < columns.length; i++) {
      const d = Math.abs(cell.x - columns[i]);
      if (d < bestDistance) {
        bestDistance = d;
        best = i;
      }
    }
    row[best] = row[best] ? `${row[best]} ${cell.text}` : cell.text;
  }
  return row;
}

export function parseTables(
  pages: PageLayout[],
  running: Set<string>,
): ExtractedTable[] {
  const found = new Map<string, ExtractedTable>();

  for (const page of pages) {
    const lines = page.lines.filter((l) => !isRunningLine(l.text, running));

    for (let i = 0; i < lines.length; i++) {
      const text = lines[i].text.replace(/\t/g, ' ');
      if (/\.{4,}/.test(text)) continue; // contents entry

      let label: string | null = null;
      let caption = '';

      const captionMatch = CAPTION.exec(text);
      const bareMatch = BARE_LABEL.exec(text);

      if (bareMatch) {
        label = normaliseLabel(bareMatch[1]);
        caption = (lines[i + 1]?.text ?? '').replace(/\t/g, ' ').trim();
        if (/\.{4,}/.test(caption) || caption.length > 120) caption = '';
      } else if (captionMatch) {
        label = normaliseLabel(captionMatch[1]);
        caption = captionMatch[2].trim();
        // A caption does not end mid-sentence with a lowercase fragment.
        if (/^[a-z]/.test(caption)) continue;
      }

      if (!label) continue;

      // Gather the block beneath the caption.
      const block: TextLine[] = [];
      const notes: string[] = [];
      let blanks = 0;

      for (let j = i + 1; j < lines.length && block.length < MAX_TABLE_LINES; j++) {
        const line = lines[j];
        if (isBlank(line)) {
          blanks += 1;
          if (blanks >= 2 && block.length > 0) break;
          continue;
        }
        if (CAPTION.test(line.text) || BARE_LABEL.test(line.text)) break;
        if (looksLikeProse(line)) {
          if (block.length === 0) continue; // caption spill-over
          break;
        }
        blanks = 0;
        block.push(line);
      }

      const gridLines = block.filter((l) => l.cells.length >= 2);
      if (gridLines.length === 0) {
        notes.push('No multi-column rows found beneath the caption.');
      }

      const columns = buildColumns(gridLines);
      const grid = gridLines.map((l) => toRow(l, columns));

      const headers = grid.length > 0 ? grid[0] : [];
      const rows = grid.slice(1);

      const numericCells: ExtractedTable['numericCells'] = [];
      for (let r = 0; r < rows.length; r++) {
        for (let c = 0; c < rows[r].length; c++) {
          const raw = rows[r][c];
          const value = parseNumericCell(raw);
          if (value !== null) numericCells.push({ row: r, col: c, value, raw });
        }
      }

      if (columns.length < 2) notes.push('Fewer than two columns detected.');
      if (rows.length === 0) notes.push('No body rows detected.');
      const ragged = grid.filter((r) => r.filter(Boolean).length < 2).length;
      if (grid.length > 0 && ragged / grid.length > 0.4) {
        notes.push('Most rows resolved to a single column; alignment is doubtful.');
      }
      if (rows.length > 0 && numericCells.length === 0) {
        notes.push('No numeric cells parsed.');
      }

      const table: ExtractedTable = {
        id: `table-${labelKey(label)}`,
        label,
        page: page.number,
        caption,
        headers,
        rows,
        numericCells,
        confidence: notes.length === 0 ? 'high' : 'low',
        confidenceNotes: notes,
      };

      // The same label can appear as a cross-reference and as the real caption.
      // Keep whichever carries more structure.
      const existing = found.get(table.id);
      if (
        !existing ||
        existing.rows.length < table.rows.length ||
        (existing.caption === '' && table.caption !== '')
      ) {
        found.set(table.id, table);
      }
    }
  }

  return [...found.values()].sort((a, b) =>
    a.page === b.page ? a.label.localeCompare(b.label) : a.page - b.page,
  );
}
